import React, { useState, useTransition } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ClassMenu } from "../../config/page_route";
import colors from "../../design/colors";
import fonts from "../../design/typography";
import { rootStore } from "../../mobx/store.js";
import { ChevronDown, ChevronRight } from "../icons/chevron";
import { observer } from "mobx-react";
import Close from "../icons/close";
import { deviceWidth } from "../../design/defaultTheme";
import { useWindowSize } from "@/hooks/useWindowSize";
import IconLogo from "../icons/logo";
interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}
// 필요한 기타 import들...

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const userType = rootStore.getProfile?.userType ?? "STUDENT";
  const isCorpMode = rootStore.getCorpMode;
  const navigate = useNavigate();
  const [, startTransition] = useTransition();

  const updatedMenu = ClassMenu.map((menu) => {
    if (menu.user_type && menu.user_type !== userType) return null;

    if (menu.hasOwnProperty("children")) {
      const updatedChildren = menu.children.filter((child) => {
        return !(child.user_type && child.user_type !== userType);
      });
      return { ...menu, children: updatedChildren };
    }

    return menu;
  }).filter((menu) => menu !== null);

  const currentPath = useLocation().pathname;
  const initialMenuState = updatedMenu.map(() => true);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean[]>(initialMenuState);
  const [hoverdIndex, setHoverdIndex] = useState<number[]>([-1, -1]);

  const isSelected = (menu: any) => {
    return menu.children?.some((child) =>
      child.containPath.some((p) =>
        new RegExp(`^${p.replace(/:[^\s/]+/g, "[^/]+")}$`).test(currentPath)
      )
    );
  };

  const handleNavigate = (path: string) => {
    if (onClose) onClose();
    startTransition(() => {
      navigate(path);
    });
  };

  const { width } = useWindowSize();
  const isMobile = width ? width < deviceWidth.mobile : false;
  const isTablet = width ? width <= deviceWidth.tablet : false;

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <Container $isOpen={isOpen}>
        <div style={{ position: "relative" }}>
          {isMobile && isTablet && (
            <CloseButton onClick={onClose}>
              <Close width="24px" height="24px" color={colors.gray900} />
            </CloseButton>
          )}
        </div>

        <div
          style={{ cursor: "pointer" }}
          onClick={() => handleNavigate("/")}
          className="navbar-brand"
        >
          <IconLogo
            width={isMobile ? "55%" : "100%"}
            height={isMobile ? "22px" : "25px"}
          />
        </div>

        <SidebarMenuWrapper>
          {updatedMenu.map((menu, i) => (
            <React.Fragment key={i}>
              <SidebarMenuMain
                $isHovered={hoverdIndex[0] === i && hoverdIndex[1] === -1}
                $isSelected={isSelected(menu)}
                onMouseEnter={() => setHoverdIndex([i, -1])}
                onMouseLeave={() => setHoverdIndex([-1, -1])}
                onClick={() =>
                  setIsMenuOpen(
                    isMenuOpen.map((_, index) =>
                      index === i ? !isMenuOpen[i] : isMenuOpen[index]
                    )
                  )
                }
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(
                      isMenuOpen.map((_, index) =>
                        index === i ? !isMenuOpen[i] : isMenuOpen[index]
                      )
                    );
                  }}
                  style={{ cursor: "pointer", marginRight: 6 }}
                >
                  {isMenuOpen[i] ? (
                    <ChevronDown
                      width="20px"
                      height="20px"
                      color={colors.gray200}
                    />
                  ) : (
                    <ChevronRight
                      width="20px"
                      height="20px"
                      color={colors.gray200}
                    />
                  )}
                </div>

                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigate(menu.path);
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    cursor: "pointer",
                    flex: 1,
                  }}
                >
                  {isSelected(menu) ? menu.iconActive : menu.iconInactive}
                  <div
                    className="menu-title"
                    style={{
                      ...fonts.body3SemiBold,
                      color: isSelected(menu) ? colors.blue600 : colors.gray500,
                      marginLeft: 8,
                    }}
                  >
                    {menu.title}
                  </div>
                </div>
              </SidebarMenuMain>

              {isMenuOpen[i] && (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    gap: 8,
                    marginBottom: 20,
                  }}
                >
                  {menu.children.map((child, j) => {
                    const selected = child.containPath.some((p) =>
                      new RegExp(`^${p.replace(/:[^\s/]+/g, "[^/]+")}$`).test(
                        currentPath
                      )
                    );
                    return (
                      <SidebarMenuSub
                        key={j}
                        isHovered={hoverdIndex[0] === i && hoverdIndex[1] === j}
                        isSelected={selected}
                        onMouseEnter={() => setHoverdIndex([i, j])}
                        onMouseLeave={() => setHoverdIndex([-1, -1])}
                        onClick={() => handleNavigate(child.path)}
                      >
                        <div
                          className="submenu-title"
                          style={{
                            ...(selected
                              ? fonts.body3SemiBold
                              : fonts.body3Medium),
                            color: selected ? colors.blue600 : colors.gray500,
                            marginLeft: 40,
                          }}
                        >
                          {child.title}
                        </div>
                      </SidebarMenuSub>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          ))}
        </SidebarMenuWrapper>
      </Container>
    </>
  );
};

export default observer(Sidebar);

const Container = styled.div<{ $isOpen?: boolean }>`
  width: 200px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  padding: 25px 20px 0 20px;
  border-right: 1px solid ${colors.gray100};
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;

  ${({ theme }) => theme.device.mobile} {
    display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
    width: 80%;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  }

  ${({ theme }) => theme.device.tablet} {
    display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
    width: 200px;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0px;
  right: -10px;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  z-index: 1001;

  ${({ theme }) => theme.device.mobile} {
    display: block;
  }
`;

const Overlay = styled.div<{ $isOpen?: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;

  ${({ theme }) => theme.device.mobile} {
    display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  }
`;

const SidebarMenuWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-top: 40px;
  padding-bottom: 40px;
`;

const SidebarMenuMain = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isHovered" && prop !== "isSelected",
})<{ $isHovered?: boolean; $isSelected?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  border-radius: 8px;
  padding: 0 8px;
  height: 40px;
  background-color: ${({ $isHovered, $isSelected }) =>
    $isHovered ? colors.gray50 : colors.white};
  margin-bottom: 8px;
  cursor: pointer;
`;

const SidebarMenuSub = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isHovered" && prop !== "isSelected",
})<{ isHovered?: boolean; isSelected?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 34px;
  background-color: ${({ isHovered, isSelected }) =>
    isHovered || isSelected ? colors.gray50 : colors.white};
  border-radius: 8px;
  cursor: pointer;
`;

const StyledLogo = styled.img`
  width: 100%;
  height: 25px;
  margin-left: 4px;

  ${({ theme }) => theme.device.mobile} {
    width: 55%;
    height: 22px;
  }
`;
